import React from 'react';
import { useNavigate, useParams } from 'react-router';
import styled from 'styled-components';
import { useMemberQuery } from '../../../graphql/types';
import { TrackingEventData } from '../../../hooks/useTracking';
import { BORDER_GRAY, WHITE } from '../../../theme';
import {
  EventCategories,
  EventNames,
  EventProfileCardNames,
} from '../../../utils/trackingEvents';
import { LinkButton } from '../Button/LinkButton';
import { ConfirmationModal } from '../ConfirmationModal';
import Text, { TextKind } from '../Text';

export const StyledHeader = styled.div`
  display: flex;
  justify-content: space-between;

  padding: 16px 24px;
  border-bottom: 1px solid ${BORDER_GRAY};
  background: ${WHITE};

  position: sticky;
  top: 0;
  z-index: 10;
`;

interface FeatureHeaderProps {
  name: string;
  trackingData?: TrackingEventData;
  state: 'loading' | 'create' | 'edit' | 'view';
  hasUnsavedChanges?: boolean;
}

export const FeatureHeader: React.FC<FeatureHeaderProps> = ({
  name,
  trackingData,
  state,
  hasUnsavedChanges,
}) => {
  const navigate = useNavigate();
  const { memberId } = useParams();
  const { data: memberData } = useMemberQuery({
    variables: { id: memberId! },
    skip: !memberId,
  });

  const [confirmationModalIsOpen, setConfirmationModalIsOpen] =
    React.useState(false);

  const onExitClick = () => {
    if (hasUnsavedChanges) {
      setConfirmationModalIsOpen(true);
      return;
    }
    navigate(-1);
  };

  const handleConfirmExit = () => {
    navigate(-1);
    setConfirmationModalIsOpen(false);
  };

  let title = '';
  if (state === 'create') {
    title = `New ${name} for ${memberData?.user?.firstName}`;
  } else if (state === 'edit') {
    title = `Editing ${name}`;
  } else if (state === 'view') {
    title = `Viewing ${name}`;
  }

  return (
    <>
      <StyledHeader>
        <Text kind={TextKind.TitleXs}>{title}</Text>
        <LinkButton
          onClick={onExitClick}
          trackingData={{
            eventName: EventNames.BackButtonClick,
            event_label: 'Exit',
            member_id: memberId,
            ...trackingData,
          }}
        >
          <Text kind={TextKind.ButtonM}>Exit</Text>
        </LinkButton>
      </StyledHeader>
      <ConfirmationModal
        isOpen={confirmationModalIsOpen}
        onConfirm={handleConfirmExit}
        confirmLabel="Yes, Cancel"
        cancelLabel="Nevermind"
        description="You have unsaved changes."
        title="Are you sure you want to cancel?"
        onClose={() => setConfirmationModalIsOpen(false)}
        trackingData={{
          ...trackingData,
          event_category:
            trackingData?.event_category ?? EventCategories.TrainingPlans,
          member_id: memberId,
          card_name: EventProfileCardNames.CancelConfirmModal,
        }}
      />
    </>
  );
};
